import { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react'
import { WorkbenchSqlEditor } from './WorkbenchSqlEditor'
import { ChromeTabs, type ChromeTab } from '@/components/common/ChromeTabs'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { loadTabs } from '@/utils/queryStorage'
import { subscribeToCollectionEvent, COLLECTION_EVENTS, type CollectionEventDetail } from '@/utils/collectionEvents'

export interface EditorTab {
  id: string
  label: string
  content: string
  isDirty?: boolean
  savedQueryId?: string
  savedQueryCollectionId?: string
  savedQueryName?: string
  databaseName?: string
  schemaName?: string
}

export interface TabbedSqlEditorRef {
  markTabClean: (tabId: string) => void
  formatActiveTab: (formatter: (content: string) => string) => void
  addTab: (content: string, label?: string) => void
}

interface TabbedSqlEditorProps {
  onExecute: (query: string, type?: 'all' | 'block') => void
  className?: string
  initialTabs?: EditorTab[]
  initialContent?: string
  onTabsChange?: (tabs: EditorTab[], activeTabId: string) => void
  onActiveQueryChange?: (query: string) => void
  onTabSelect?: (tab: EditorTab) => void
  datasourceId?: string
  databaseName?: string
  schemaName?: string
  databaseType?: string
  shouldCreateNewTab?: boolean
  onNewTabCreated?: () => void
  onSaveQuery?: (tab: EditorTab) => Promise<void>
  savedQueryInfo?: {
    savedQueryId?: string
    savedQueryCollectionId?: string
    savedQueryName?: string
    databaseName?: string
    schemaName?: string
  }
}

export const TabbedSqlEditor = forwardRef<TabbedSqlEditorRef, TabbedSqlEditorProps>(({
  onExecute,
  className = '',
  initialTabs,
  initialContent,
  onTabsChange,
  onActiveQueryChange,
  onTabSelect,
  datasourceId,
  databaseName,
  schemaName,
  databaseType,
  shouldCreateNewTab,
  onNewTabCreated,
  onSaveQuery,
  savedQueryInfo
}, ref) => {
  // Track the current datasource to detect changes
  const [currentDatasourceId, setCurrentDatasourceId] = useState(datasourceId)
  
  // Track when we're switching tabs to prevent race conditions
  const isTabSwitchingRef = useRef(false)
  
  // Track initial notification to prevent render loops
  const hasNotifiedInitialStateRef = useRef(false)
  
  // Track whether we've already applied initialContent to prevent interference with user deletion
  const hasAppliedInitialContentRef = useRef(false)
  
  // Initialize tabs and activeTabId
  const initializeTabs = useCallback((dsId?: string) => {
    if (dsId) {
      const savedTabs = loadTabs(dsId)
      if (savedTabs && savedTabs.tabs.length > 0) {
        return {
          tabs: savedTabs.tabs,
          activeTabId: savedTabs.activeTabId
        }
      }
    }
    const defaultTabs = initialTabs || [{ id: '1', label: 'Query 1', content: initialContent || '' }]
    return {
      tabs: defaultTabs,
      activeTabId: defaultTabs[0]?.id || '1'
    }
  }, [initialContent, initialTabs])
  
  const [tabState, setTabState] = useState(() => initializeTabs(datasourceId))
  const { tabs, activeTabId } = tabState
  
  // State for confirmation modal
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
    isDangerous?: boolean
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isDangerous: false
  })
  
  // Helper to update both tabs and activeTabId
  const updateTabState = (newTabs: EditorTab[], newActiveTabId: string) => {
    setTabState({ tabs: newTabs, activeTabId: newActiveTabId })
  }
  
  const tabCounter = useRef(
    Math.max(...tabs.map(t => Number.parseInt(t.id, 10) || 0), 0) + 1
  )
  
  // Track the current confirmation resolver for tab close
  const confirmationResolver = useRef<((result: boolean) => void) | null>(null)
  
  

  const activeTab = tabs.find(tab => tab.id === activeTabId)
  
  // Helper to show confirmation modal
  const showConfirmation = (title: string, message: string, onConfirm: () => void, isDangerous?: boolean) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      isDangerous
    })
  }
  
  // Helper to close confirmation modal
  const closeConfirmation = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }))
    // If there's a pending resolver, resolve it with false (cancelled)
    if (confirmationResolver.current) {
      confirmationResolver.current(false)
      confirmationResolver.current = null
    }
  }

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    markTabClean: (tabId: string) => {
      const updatedTabs = tabs.map(tab => 
        tab.id === tabId 
          ? { ...tab, isDirty: false }
          : tab
      )
      updateTabState(updatedTabs, activeTabId)
      onTabsChange?.(updatedTabs, activeTabId)
    },
    formatActiveTab: (formatter: (content: string) => string) => {
      const updatedTabs = tabs.map(tab => 
        tab.id === activeTabId 
          ? { ...tab, content: formatter(tab.content), isDirty: true }
          : tab
      )
      updateTabState(updatedTabs, activeTabId)
      onTabsChange?.(updatedTabs, activeTabId)
      
      // Update the active query change callback with the formatted content
      const activeTabContent = updatedTabs.find(t => t.id === activeTabId)?.content
      if (activeTabContent) {
        onActiveQueryChange?.(activeTabContent)
      }
    },
    addTab: (content: string, label?: string) => {
      const newTab: EditorTab = {
        id: String(tabCounter.current++),
        label: label || `Query ${tabCounter.current - 1}`,
        content,
        isDirty: false // New tabs with content from AI start as not dirty initially
      }
      const newTabs = [...tabs, newTab]
      updateTabState(newTabs, newTab.id)
      onTabsChange?.(newTabs, newTab.id)
      
      // Update the active query change callback with the new content
      onActiveQueryChange?.(content)
    }
  }), [tabs, activeTabId, onTabsChange, onActiveQueryChange])

  // Handle datasource changes
  useEffect(() => {
    if (datasourceId !== currentDatasourceId) {
      setCurrentDatasourceId(datasourceId)
      const { tabs: newTabs, activeTabId: newActiveTabId } = initializeTabs(datasourceId)
      updateTabState(newTabs, newActiveTabId)
      // Update tab counter based on loaded tabs
      tabCounter.current = Math.max(...newTabs.map(t => Number.parseInt(t.id, 10) || 0), 0) + 1
      
      // Reset the initial notification flag for new datasource
      hasNotifiedInitialStateRef.current = false
      // Reset the initialContent application flag for new datasource
      hasAppliedInitialContentRef.current = false
      
      // Use setTimeout to defer callbacks and break render loops
      setTimeout(() => {
        onTabsChange?.(newTabs, newActiveTabId)
        const activeTabContent = newTabs.find(t => t.id === newActiveTabId)?.content
        if (activeTabContent) {
          onActiveQueryChange?.(activeTabContent)
        }
      }, 0)
    }
  }, [datasourceId, currentDatasourceId, initializeTabs, onActiveQueryChange, onTabsChange])

  // Notify parent about tab state changes (including initial state)
  useEffect(() => {
    // Use setTimeout to defer callbacks and break render loops
    const timeoutId = setTimeout(() => {
      if (!hasNotifiedInitialStateRef.current) {
        // First notification - notify about initial tabs
        hasNotifiedInitialStateRef.current = true
        onTabsChange?.(tabs, activeTabId)
      }
      
      // Notify about active query content changes
      const activeTabContent = tabs.find(t => t.id === activeTabId)?.content
      if (activeTabContent !== undefined) {
        onActiveQueryChange?.(activeTabContent)
      }
    }, 0)
    
    return () => { clearTimeout(timeoutId) }
  }, [tabs, activeTabId, onTabsChange, onActiveQueryChange])

  // Update tabs when initialContent changes (e.g., from URL params)
  // Only apply initialContent once during setup, don't prevent user from emptying content
  useEffect(() => {
    if (initialContent && tabs.length === 1 && tabs[0].content === '' && !hasAppliedInitialContentRef.current) {
      // Only set initialContent once, then allow user full control
      const newTabs = [{ ...tabs[0], content: initialContent }]
      updateTabState(newTabs, activeTabId)
      onTabsChange?.(newTabs, activeTabId)
      hasAppliedInitialContentRef.current = true
    }
  }, [initialContent, tabs, activeTabId, onTabsChange])

  // Create new tab when shouldCreateNewTab is true
  useEffect(() => {
    if (shouldCreateNewTab && initialContent) {
      // Check if a tab with the same saved query info already exists
      if (savedQueryInfo?.savedQueryId) {
        const existingTabIndex = tabs.findIndex(tab => 
          tab.savedQueryId === savedQueryInfo.savedQueryId &&
          tab.savedQueryCollectionId === savedQueryInfo.savedQueryCollectionId
        )
        
        if (existingTabIndex >= 0) {
          // Update existing tab instead of creating new one
          const existingTab = tabs[existingTabIndex]
          const updatedTab = {
            ...existingTab,
            content: initialContent,
            label: savedQueryInfo.savedQueryName || existingTab.label,
            savedQueryId: savedQueryInfo.savedQueryId,
            savedQueryCollectionId: savedQueryInfo.savedQueryCollectionId,
            savedQueryName: savedQueryInfo.savedQueryName,
            databaseName: savedQueryInfo.databaseName,
            schemaName: savedQueryInfo.schemaName,
            isDirty: false
          }
          
          const updatedTabs = Array.from(tabs)
          updatedTabs[existingTabIndex] = updatedTab
          
          updateTabState(updatedTabs, existingTab.id)
          onTabsChange?.(updatedTabs, existingTab.id)
          onNewTabCreated?.()
          return
        }
      }
      
      // Create a new tab with the content from URL
      const newTabId = String(tabCounter.current++)
      
      // Extract table name from SELECT query
      let tabLabel = 'Query'
      const fromMatch = /FROM\s+([^\s;]+)/i.exec(initialContent)
      if (fromMatch) {
        const [, tableName] = fromMatch
        // Remove schema prefix if present (e.g., "schema.table" -> "table")
        const cleanTableName = tableName.includes('.') ? tableName.split('.').pop() : tableName
        tabLabel = cleanTableName || 'Query'
      }
      
      const newTab: EditorTab = {
        id: newTabId,
        label: savedQueryInfo?.savedQueryName || `${tabLabel} ${tabCounter.current - 1}`,
        content: initialContent,
        isDirty: !!initialContent && !savedQueryInfo?.savedQueryId,
        savedQueryId: savedQueryInfo?.savedQueryId,
        savedQueryCollectionId: savedQueryInfo?.savedQueryCollectionId,
        savedQueryName: savedQueryInfo?.savedQueryName,
        databaseName: savedQueryInfo?.databaseName,
        schemaName: savedQueryInfo?.schemaName
      }
      
      const newTabs = [...tabs, newTab]
      updateTabState(newTabs, newTabId)
      onTabsChange?.(newTabs, newTabId)
      onNewTabCreated?.()
    }
  }, [shouldCreateNewTab, initialContent, savedQueryInfo, tabs, onTabsChange, onNewTabCreated])


  const handleTabChange = (tabId: string) => {
    const targetTab = tabs.find(t => t.id === tabId)
    if (!targetTab) {
      console.warn('Target tab not found:', tabId)
      return
    }
    
    // Set flag to prevent handleQueryChange from interfering during switch
    isTabSwitchingRef.current = true
    
    // Update the tab state first
    updateTabState(tabs, tabId)
    
    // Immediately notify parent to save active tab change to localStorage
    onTabsChange?.(tabs, tabId)
    
    // Notify parent about tab selection with database/schema info
    onTabSelect?.(targetTab)
    
    // Then update the query content to match the new tab
    onActiveQueryChange?.(targetTab.content)
    
    // Clear the flag after a brief delay to allow editor to settle
    setTimeout(() => {
      isTabSwitchingRef.current = false
    }, 100)
  }

  const handleTabClose = (tabId: string) => {
    const tabIndex = tabs.findIndex(tab => tab.id === tabId)
    if (tabs.length === 1) {
      // Don't close the last tab, just clear it and remove saved query metadata
      const newTabs = [{ id: '1', label: 'Query 1', content: '', isDirty: false }]
      updateTabState(newTabs, activeTabId)
      onTabsChange?.(newTabs, activeTabId)
      return
    }

    const newTabs = tabs.filter(tab => tab.id !== tabId)
    // If we closed the active tab, switch to adjacent tab
    let newActiveTabId = activeTabId
    if (tabId === activeTabId) {
      const newActiveIndex = Math.max(0, tabIndex - 1)
      newActiveTabId = newTabs[newActiveIndex].id
    }
    
    updateTabState(newTabs, newActiveTabId)
    onTabsChange?.(newTabs, newActiveTabId)
  }

  const handleTabDuplicate = (tabId: string) => {
    const tabToDuplicate = tabs.find(tab => tab.id === tabId)
    if (!tabToDuplicate) return
    
    const newTab: EditorTab = {
      id: String(tabCounter.current++),
      label: `${tabToDuplicate.label} (copy)`,
      content: tabToDuplicate.content,
      isDirty: false
    }
    
    const newTabs = [...tabs, newTab]
    updateTabState(newTabs, newTab.id)
    onTabsChange?.(newTabs, newTab.id)
  }

  const handleCloseAllTabs = () => {
    // Check if any tabs are dirty and need confirmation
    const dirtyTabs = tabs.filter(tab => tab.isDirty)
    if (dirtyTabs.length > 0) {
      const tabWord = dirtyTabs.length === 1 ? 'tab' : 'tabs'
      showConfirmation(
        'Close All Tabs',
        `You have ${dirtyTabs.length} unsaved ${tabWord}. Are you sure you want to close all tabs? Any unsaved changes will be lost.`,
        () => {
          // Keep one empty tab
          const newTabs = [{ id: '1', label: 'Query 1', content: '', isDirty: false }]
          tabCounter.current = 2
          updateTabState(newTabs, newTabs[0].id)
          onTabsChange?.(newTabs, newTabs[0].id)
        },
        true
      )
    } else {
      // Keep one empty tab
      const newTabs = [{ id: '1', label: 'Query 1', content: '', isDirty: false }]
      tabCounter.current = 2
      updateTabState(newTabs, newTabs[0].id)
      onTabsChange?.(newTabs, newTabs[0].id)
    }
  }

  const handleCloseOtherTabs = (keepTabId: string) => {
    // Check if any other tabs are dirty and need confirmation
    const otherTabs = tabs.filter(tab => tab.id !== keepTabId)
    const dirtyOtherTabs = otherTabs.filter(tab => tab.isDirty)
    if (dirtyOtherTabs.length > 0) {
      const tabWord = dirtyOtherTabs.length === 1 ? 'tab' : 'tabs'
      showConfirmation(
        'Close Other Tabs',
        `You have ${dirtyOtherTabs.length} unsaved ${tabWord}. Are you sure you want to close all other tabs? Any unsaved changes will be lost.`,
        () => {
          const newTabs = tabs.filter(tab => tab.id === keepTabId)
          updateTabState(newTabs, keepTabId)
          onTabsChange?.(newTabs, keepTabId)
        },
        true
      )
    } else {
      const newTabs = tabs.filter(tab => tab.id === keepTabId)
      updateTabState(newTabs, keepTabId)
      onTabsChange?.(newTabs, keepTabId)
    }
  }

  const handleForceCloseAllTabs = () => {
    // Force close all tabs without confirmation
    const newTabs = [{ id: '1', label: 'Query 1', content: '', isDirty: false }]
    tabCounter.current = 2
    updateTabState(newTabs, newTabs[0].id)
    onTabsChange?.(newTabs, newTabs[0].id)
  }

  const handleConfirmClose = async (tabId: string): Promise<boolean> => {
    const tab = tabs.find(t => t.id === tabId)
    if (tab?.isDirty) {
      // eslint-disable-next-line promise/avoid-new
      return new Promise<boolean>((resolve) => {
        confirmationResolver.current = resolve
        showConfirmation(
          'Close Tab',
          `Tab "${tab.label}" has unsaved changes. Are you sure you want to close it? Any unsaved changes will be lost.`,
          () => {
            confirmationResolver.current = null
            resolve(true)
          },
          true
        )
      })
    }
    return true
  }

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        
        // Use setState callback to get the current active tab
        let currentActiveTab: EditorTab | undefined
        
        // eslint-disable-next-line promise/avoid-new
        await new Promise<void>((resolve) => {
          setTabState(currentState => {
            currentActiveTab = currentState.tabs.find(tab => tab.id === currentState.activeTabId)
            resolve()
            return currentState
          })
        })
        
        if (currentActiveTab && onSaveQuery) {
          await onSaveQuery(currentActiveTab)
          // Don't mark tab as clean here - it will be done by either:
          // 1. markTabClean call from parent for existing queries
          // 2. QUERY_CREATED event handler for new queries
        }
      }
    }

    const handleKeyDownWrapper = (e: KeyboardEvent) => {
      void handleKeyDown(e)
    }
    window.addEventListener('keydown', handleKeyDownWrapper)
    return () => {
      window.removeEventListener('keydown', handleKeyDownWrapper)
    }
  }, [onSaveQuery, onTabsChange])

  // Listen for query creation events from drag-drop saves
  useEffect(() => {
    const handleQueryCreated = (event: CustomEvent<CollectionEventDetail>) => {
      const { tabId, queryId, collectionId, queryName } = event.detail
      
      // Use setState callback to ensure we get the latest state
      if (tabId && queryId) {
        setTabState(currentState => {
          const currentTabs = currentState.tabs
          const tabIndex = currentTabs.findIndex(tab => tab.id === tabId)
          
          if (tabIndex !== -1) {
            // Update the tab to mark it as saved
            const updatedTabs = Array.from(currentTabs)
            updatedTabs[tabIndex] = {
              ...updatedTabs[tabIndex],
              isDirty: false,
              savedQueryId: queryId,
              savedQueryCollectionId: collectionId,
              savedQueryName: queryName || updatedTabs[tabIndex].label
            }
            
            onTabsChange?.(updatedTabs, currentState.activeTabId)
            return { ...currentState, tabs: updatedTabs }
          } else {
            return currentState
          }
        })
      }
    }

    return subscribeToCollectionEvent(
      COLLECTION_EVENTS.QUERY_CREATED,
      handleQueryCreated
    )
  }, [onTabsChange])

  const handleAddTab = () => {
    const newTab: EditorTab = {
      id: String(tabCounter.current++),
      label: `Query ${tabCounter.current - 1}`,
      content: '',
      isDirty: false // New empty tabs start as not dirty
    }
    const newTabs = [...tabs, newTab]
    updateTabState(newTabs, newTab.id)
    onTabsChange?.(newTabs, newTab.id)
  }

  const handleQueryChange = (value: string) => {
    // Skip updates if we're in the middle of switching tabs
    if (isTabSwitchingRef.current) {
      return
    }
    
    // Use setState callback to get fresh state and ensure atomic updates
    setTabState(currentState => {
      // Find the currently active tab
      const activeTabIndex = currentState.tabs.findIndex(tab => tab.id === currentState.activeTabId)
      if (activeTabIndex === -1) {
        console.warn('Active tab not found, skipping content update')
        return currentState // Don't update if active tab doesn't exist
      }
      
      const activeTab = currentState.tabs[activeTabIndex]
      const trimmedValue = (value || '').trim()
      
      // Create new tabs array with only the active tab updated
      const newTabs = Array.from(currentState.tabs)
      newTabs[activeTabIndex] = { 
        ...activeTab, 
        content: value || '', // Ensure empty string is handled properly
        isDirty: trimmedValue.length > 0 // Only mark as dirty if content is not empty
      }
      
      // Defer callbacks to avoid render loops
      setTimeout(() => {
        onTabsChange?.(newTabs, currentState.activeTabId)
        onActiveQueryChange?.(value)
      }, 0)
      
      return { ...currentState, tabs: newTabs }
    })
  }

  const handleExecute = (query: string, type?: 'all' | 'block') => {
    // Simply execute the query without changing the dirty state
    onExecute(query, type)
  }

  // Convert tabs to ChromeTab interface for ChromeTabs component
  const tabsForComponent: ChromeTab[] = tabs.map(tab => ({
    id: tab.id,
    label: tab.label,
    closeable: true,
    isDirty: tab.isDirty,
    content: tab.content,
    savedQueryId: tab.savedQueryId,
    savedQueryCollectionId: tab.savedQueryCollectionId,
    savedQueryName: tab.savedQueryName,
    databaseName: tab.databaseName,
    schemaName: tab.schemaName
  }))

  return (
    <div className={`flex flex-col h-full min-w-0 w-full ${className}`}>
      <ChromeTabs
        tabs={tabsForComponent}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        onTabClose={(tabId) => { handleTabClose(tabId) }}
        onTabAdd={handleAddTab}
        onConfirmClose={handleConfirmClose}
        onTabDuplicate={handleTabDuplicate}
        onCloseAllTabs={() => { handleCloseAllTabs() }}
        onCloseOtherTabs={(keepTabId) => { handleCloseOtherTabs(keepTabId) }}
        onForceCloseAllTabs={handleForceCloseAllTabs}
        className="border-b border-gray-200 dark:border-gray-700"
        enableDragDrop={true}
        datasourceId={datasourceId}
        databaseName={databaseName}
        schemaName={schemaName}
        reserveAddButtonSpace={false}
      />
      
      {activeTab && (
        <div className="flex-1 overflow-hidden">
          <WorkbenchSqlEditor
            key={activeTab.id}
            value={activeTab.content}
            onChange={handleQueryChange}
            onExecuteQuery={(query) => { handleExecute(query, 'block'); }}
            datasourceId={datasourceId}
            databaseName={databaseName}
            schemaName={schemaName}
            databaseType={databaseType}
          />
        </div>
      )}
      
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.isDangerous ? 'Close' : 'OK'}
        cancelText="Cancel"
        variant={confirmationModal.isDangerous ? 'danger' : 'warning'}
        isDangerous={confirmationModal.isDangerous}
      />
    </div>
  )
})

TabbedSqlEditor.displayName = 'TabbedSqlEditor'